/**
 * @fileoverview SMS thread hook - fetches and polls Communication resources for patient.
 * @agentic connections
 */

// Constants
import {
  SMS_THREAD_INITIAL_COUNT,
  SMS_THREAD_POLL_COUNT,
  SMS_THREAD_PAGE_COUNT,
  COMMUNICATION_TYPE_SYSTEM,
  LEGACY_COMMUNICATION_CHANNEL_SYSTEM,
} from "@benchmark/core/constants";

// Hooks
import { useMemo, useCallback, useEffect, useState, useRef } from "react";
import { useMedplum } from "@medplum/react";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

// Events
import { DomainEventNames } from "@benchmark/core/events";

// Types
import type { Communication } from "@medplum/fhirtypes";
import type { UseSMSThreadResult } from "@/types/domain/messaging";

/**
 * Category token matching patient messages across the canonical
 * (`communication-type|patient-message`, covering both SMS and calls) and the
 * legacy channel systems, so the thread stays correct through the
 * standardization backfill window. Manual outreach (`manual-outreach`) is
 * intentionally excluded.
 * @agentic connections
 */
const PATIENT_MESSAGE_CATEGORY_QUERY = `${COMMUNICATION_TYPE_SYSTEM}|patient-message,${LEGACY_COMMUNICATION_CHANNEL_SYSTEM}|sms,${LEGACY_COMMUNICATION_CHANNEL_SYSTEM}|phone`;

/**
 * Merges `incoming` Communications into `existing`, deduplicating by `id`
 * (incoming wins so updates replace their prior copy) and keeping the store's
 * canonical newest-first (`-sent`) order. Used for both incremental polling
 * (new/updated messages at the newest end) and reverse pagination (older
 * messages appended at the oldest end) so the two paths share one merge.
 * @agentic connections
 */
function mergeById(
  existing: Communication[],
  incoming: Communication[],
): Communication[] {
  if (incoming.length === 0) {
    return existing;
  }

  const existingIds = new Set(existing.map((m) => m.id));
  const updated = existing.map(
    (current) => incoming.find((m) => m.id === current.id) ?? current,
  );
  const newOnly = incoming.filter((m) => !existingIds.has(m.id));

  return [...updated, ...newOnly].sort((a, b) => {
    const dateA = new Date(a.sent || 0).getTime();
    const dateB = new Date(b.sent || 0).getTime();
    return dateB - dateA; // newest first
  });
}

/**
 * Fetches SMS/phone Communications for patient; supports polling with incremental fetch.
 * @agentic connections
 */
export function useSMSThread(
  patientId: string | undefined,
  options: {
    /** Enable polling for real-time updates (default: false) */
    enablePolling?: boolean;
    /** Polling interval in milliseconds (default: 30000) */
    pollingInterval?: number;
  } = {},
): UseSMSThreadResult {
  const { enablePolling = false, pollingInterval = 30000 } = options;
  const medplum = useMedplum();

  // Local state
  const [messages, setMessages] = useState<Communication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Reverse-pagination state (older-history batches loaded on scroll-back)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);

  // Track last fetch time for incremental polling
  const lastFetchTimeRef = useRef<string | null>(null);
  const isInitialFetchRef = useRef(true);

  // Cursor for reverse pagination: the `sent` timestamp of the oldest loaded
  // message. Older pages are fetched with `sent=lt<cursor>`, which is stable
  // against new messages arriving at the newest end (unlike a numeric offset).
  const oldestCursorRef = useRef<string | null>(null);
  // Guards against overlapping older-page fetches when the scroll sentinel
  // fires repeatedly before a batch resolves.
  const isLoadingOlderRef = useRef(false);

  // Build base search params for SMS and phone communications
  const baseSearchParams = useMemo(() => {
    if (!patientId) {
      return null;
    }

    return {
      subject: `Patient/${patientId}`,
      // Canonical patient-message category (SMS + calls) plus legacy channel
      // tokens for not-yet-backfilled resources.
      category: PATIENT_MESSAGE_CATEGORY_QUERY,
      // Defense in depth: retired resources never render in the thread (SMS
      // statuses are never entered-in-error by policy, but the filter makes
      // that invariant server-enforced).
      "status:not": "entered-in-error",
      _sort: "-sent",
    };
  }, [patientId]);

  // Full fetch - used for initial load and manual refetch
  const fetchAllMessages = useCallback(async () => {
    if (!baseSearchParams) {
      setMessages([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = (await medplum.searchResources("Communication", {
        ...baseSearchParams,
        _count: SMS_THREAD_INITIAL_COUNT.toString(),
      })) as Communication[];

      setMessages(results);
      // Reset reverse-pagination anchors to the freshly loaded newest page.
      // Results are `-sent` (newest first), so the oldest is the last entry.
      oldestCursorRef.current = results[results.length - 1]?.sent ?? null;
      setHasMoreOlder(results.length === SMS_THREAD_INITIAL_COUNT);
      lastFetchTimeRef.current = new Date().toISOString();
      isInitialFetchRef.current = false;
    } catch (err) {
      setError(err as Error);
      console.error("Error fetching communication thread:", err);
    } finally {
      setIsLoading(false);
    }
  }, [medplum, baseSearchParams]);

  // Incremental fetch - used for polling (only fetches new/updated messages)
  const fetchNewMessages = useCallback(async () => {
    if (!baseSearchParams || !lastFetchTimeRef.current) {
      // Fall back to full fetch if we don't have a last fetch time
      await fetchAllMessages();
      return;
    }

    try {
      // Query only for messages updated since last fetch
      // This is much more efficient than fetching all messages
      const results = await medplum.searchResources("Communication", {
        ...baseSearchParams,
        // Only get messages created/updated since last fetch
        _lastUpdated: `gt${lastFetchTimeRef.current}`,
        _count: SMS_THREAD_POLL_COUNT.toString(),
      });

      if (results.length > 0) {
        // Merge new/updated messages, preserving any older pages already loaded
        // via reverse pagination (mergeById dedups by id and re-sorts -sent).
        setMessages((prev) => mergeById(prev, results as Communication[]));
      }

      lastFetchTimeRef.current = new Date().toISOString();
    } catch (err) {
      // Log error but don't update error state for background polls
      console.error("Error fetching new communications:", err);
    }
  }, [medplum, baseSearchParams, fetchAllMessages]);

  // Gap-free refresh used for event-driven refetch. Re-fetches the newest page
  // (same query as the initial load, so any just-created/updated resource is
  // guaranteed to be within it) and MERGES it into state — unlike the initial
  // fetchAllMessages, which replaces. Merging preserves any older pages already
  // loaded via reverse pagination (so a live write doesn't fling the reader to
  // the bottom) while still reconciling the optimistic bubble by id. Chosen over
  // the incremental fetchNewMessages here because the incremental path's
  // client-clock `_lastUpdated` watermark can miss the resource under clock skew
  // or indexing delay, which would leave a gap after the optimistic bubble is
  // dropped.
  const refreshNewestPage = useCallback(async () => {
    if (!baseSearchParams) {
      return;
    }

    try {
      const results = (await medplum.searchResources("Communication", {
        ...baseSearchParams,
        _count: SMS_THREAD_INITIAL_COUNT.toString(),
      })) as Communication[];

      setMessages((prev) => mergeById(prev, results));
      lastFetchTimeRef.current = new Date().toISOString();
    } catch (err) {
      console.error("Error refreshing communication thread:", err);
    }
  }, [medplum, baseSearchParams]);

  // Reverse pagination - load the next older batch using the `sent` cursor.
  // Prepends (by store order: appends at the oldest end) and dedups by id.
  // Resolves to whether an older-page fetch was actually issued, so the caller
  // can tell a real prepend apart from a synchronous no-op (empty thread /
  // nothing older / already loading).
  const loadOlderMessages = useCallback(async (): Promise<boolean> => {
    if (!baseSearchParams || isLoadingOlderRef.current || !hasMoreOlder) {
      return false;
    }

    const cursor = oldestCursorRef.current;
    if (!cursor) {
      // No cursor yet (empty thread or initial load not settled): nothing older.
      return false;
    }

    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);

    try {
      // `le` (not `lt`) so messages that share the boundary `sent` timestamp are
      // not skipped; mergeById dedups the already-loaded boundary row(s).
      const older = (await medplum.searchResources("Communication", {
        ...baseSearchParams,
        sent: `le${cursor}`,
        _count: SMS_THREAD_PAGE_COUNT.toString(),
      })) as Communication[];

      const newOldest = older[older.length - 1]?.sent ?? cursor;

      if (older.length > 0) {
        setMessages((prev) => mergeById(prev, older));
      }

      // A short page means we've reached the beginning of the thread. Also stop
      // when the page couldn't advance past the cursor timestamp (a full page of
      // boundary ties) so we don't re-request the same instant forever. Known
      // limitation: if a thread ever had >= SMS_THREAD_PAGE_COUNT messages at one
      // identical `sent` instant, history beyond that wall wouldn't load — not a
      // realistic case for human-paced SMS/calls, and a timestamp cursor can't
      // page past it without a secondary sort key / offset (rejected for
      // live-thread stability).
      if (older.length < SMS_THREAD_PAGE_COUNT || newOldest === cursor) {
        setHasMoreOlder(false);
      } else {
        oldestCursorRef.current = newOldest;
      }

      return true;
    } catch (err) {
      // Background action: log but keep hasMoreOlder so a later scroll retries.
      console.error("Error loading older communications:", err);
      return true;
    } finally {
      isLoadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [medplum, baseSearchParams, hasMoreOlder]);

  // Initial fetch
  useEffect(() => {
    isInitialFetchRef.current = true;
    queueMicrotask(() => {
      void fetchAllMessages();
    });
  }, [fetchAllMessages]);

  // Refresh when a Communication is written for this patient. SMS sends reach
  // this in-process emitter via the drawer's delivery effect (the webhook write
  // itself is server-side and cannot — see `mutation-refetch` rule A2), and
  // manual-outreach / progress-log writes publish the same events. We refresh
  // the newest page and MERGE (not full reset) so any older pages loaded via
  // reverse pagination survive; the new/updated resource is within the newest
  // page, so the existing optimistic-dedup drops the temp bubble seamlessly.
  // Known limitation (unchanged from the prior full-refetch behavior, which also
  // only fetched the newest page): a client-side CommunicationUpdated to a
  // message OLDER than the newest page won't live-refresh until the drawer
  // reopens. The critical paths (just-sent reconciliation, new inbound) are
  // always within the newest page, so this only affects a paginated-in old
  // message being updated in-session — rare and self-healing on reopen.
  useEventDrivenRefetch(
    [
      DomainEventNames.CommunicationCreated,
      DomainEventNames.CommunicationUpdated,
    ],
    {
      refetch: refreshNewestPage,
      enabled: !!patientId,
      filter: (payload) =>
        (payload as { patientId?: string }).patientId === patientId,
      debounceMs: 400,
    },
  );

  // Set up polling for real-time updates (incremental)
  useEffect(() => {
    if (!enablePolling || !patientId) {
      return;
    }

    const intervalId = setInterval(() => {
      // Use incremental fetch for polling - much more efficient
      fetchNewMessages();
    }, pollingInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [enablePolling, patientId, pollingInterval, fetchNewMessages]);

  return {
    messages,
    isLoading,
    error,
    refetch: fetchAllMessages,
    loadOlderMessages,
    isLoadingOlder,
    hasMoreOlder,
  };
}
